/**
 * Lazy singleton loader for OpenCV.js.
 *
 * Place opencv.js in /public/opencv.js (download from
 * https://github.com/nicholasgasior/opencv.js/releases or build from source).
 *
 * The WASM binary initializes asynchronously; this module returns a promise that
 * resolves once `cv.Mat` is available so callers never race the runtime init.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    cv: any;
    Module: any;
  }
}

const OPENCV_URL = "/opencv.js";

let loadPromise: Promise<any> | null = null;

export function loadOpenCV(): Promise<any> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<any>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("OpenCV.js requires a browser environment"));
      return;
    }

    // Already fully initialised (e.g., hot-reload).
    if (window.cv && typeof window.cv.Mat === "function") {
      resolve(window.cv);
      return;
    }

    // Register the WASM runtime-ready callback BEFORE the script fires.
    const prevOnInit: (() => void) | undefined =
      window.Module?.onRuntimeInitialized;

    window.Module = {
      ...(window.Module ?? {}),
      onRuntimeInitialized(): void {
        if (prevOnInit) prevOnInit.call(this);
        // Give the cv object one tick to finish wiring itself up.
        setTimeout(() => {
          if (window.cv && typeof window.cv.Mat === "function") {
            resolve(window.cv);
          } else {
            reject(new Error("OpenCV.js WASM initialised but cv.Mat is missing"));
          }
        }, 0);
      },
    };

    void (async (): Promise<void> => {
      try {
        const response = await fetch(OPENCV_URL, { cache: "no-store" });
        const text = await response.text();

        if (!response.ok) {
          throw new Error(
            `Failed to load OpenCV.js from ${OPENCV_URL} (HTTP ${response.status})`,
          );
        }

        const trimmed = text.trimStart();
        if (trimmed.startsWith("<")) {
          throw new Error(
            `OpenCV.js endpoint returned HTML instead of JavaScript: ${OPENCV_URL}`,
          );
        }

        const blob = new Blob([text], { type: "text/javascript" });
        const blobUrl = URL.createObjectURL(blob);
        const script = document.createElement("script");
        script.src = blobUrl;
        script.async = true;
        script.onload = (): void => {
          URL.revokeObjectURL(blobUrl);
        };
        script.onerror = (): void => {
          URL.revokeObjectURL(blobUrl);
          loadPromise = null; // allow retry on next open
          reject(new Error(`Failed to execute OpenCV.js from ${OPENCV_URL}`));
        };
        document.head.appendChild(script);
      } catch (error) {
        loadPromise = null; // allow retry on next open
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    })();
  });

  return loadPromise;
}
